import React, { useEffect, useState } from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { View, Text, ActivityIndicator, Modal, TouchableOpacity, Dimensions, Platform } from 'react-native';
import { useAuthStore } from '../stores/authStore';
import { supabase, testSupabaseConnection } from '../api/supabase';
import theme from '../theme';
import { PERMISSIONS } from '../constants/permissions';

// Auth Screens
import { LoginScreen } from '../screens/auth/LoginScreen';

// Import Navigators
import { DashboardNavigator } from './DashboardNavigator';
import { DriverNavigator } from './DriverNavigator';
import { VehicleNavigator } from './VehicleNavigator';
import { JobCardNavigator } from './JobCardNavigator';
import { AssignmentNavigator } from './AssignmentNavigator';

// Import Maintenance Screens
import { MaintenanceScreen } from '../screens/maintenance/MaintenanceScreen';
import { MaintenanceDashboardScreen } from '../screens/maintenance/MaintenanceDashboardScreen';
import { JobCardsScreen } from '../screens/maintenance/JobCardsScreen';
import { CreditReportScreen } from '../screens/maintenance/CreditReportScreen';
import { AddJobCardScreen } from '../screens/maintenance/AddJobCardScreen';
import { MaintenanceDetailsScreen } from '../screens/maintenance/MaintenanceDetailsScreen';
import WorkshopCreditReportScreen from '../screens/maintenance/WorkshopCreditReportScreen';
import { VehicleAssignmentScreen } from '../screens/assignment/VehicleAssignmentScreenNew';
import { AssignmentHistoryScreen } from '../screens/assignment/AssignmentHistoryScreen';
import { AssignmentScreen } from '../screens/assignment/AssignmentScreen';
import { AssignmentDetailsScreen } from '../screens/assignment/AssignmentDetailsScreen';

// Analytics Screen
import AnalyticsScreen from '../screens/analytics/AnalyticsScreen';

// Settings Screens
import { SettingsScreen } from '../screens/settings/SettingsScreen';
import { ActivityLogScreen } from '../screens/settings/ActivityLogScreen';
import { VehicleRateSettingsScreen } from '../screens/vehicle/VehicleRateSettingsScreen';
import { BackupExportScreen } from '../screens/settings/BackupExportScreen';
import { UserManagementScreenNew as UserManagementScreen } from '../screens/settings/UserManagementScreenNew';
import { DisplayOptionsScreen } from '../screens/settings/DisplayOptionsScreen';
import { DriverSyncScreen } from '../screens/settings/DriverSyncScreen';

// HR Management Screens
import { EmployeeListScreen } from '../screens/hr/EmployeeListScreen';

// Import Screens for Navigation
import ManageAssignmentScreen from '../screens/assignment/ManageAssignmentScreen';
import EditAssignmentScreen from '../screens/assignment/EditAssignmentScreen';

// Import Accounting Screens
import { AccountingScreen } from '../screens/accounting/AccountingScreen';
import AccountingViewScreen from '../screens/AccountingViewScreen';
import { DataImportScreen } from '../screens/accounting/DataImportScreen';
import MonthlySettlementScreen from '../screens/accounting/MonthlySettlementScreen';
import { PaymentHistoryScreen } from '../screens/accounting/PaymentHistoryScreen';
import { RentDiscountScreen } from '../screens/accounting/RentDiscountScreen';
import { SettlementDetailScreen } from '../screens/accounting/SettlementDetailScreen';
import { CompanyProfitScreen } from '../screens/accounting/CompanyProfitScreen';

// Import Analytics Screens
import { AnalyticsOverviewScreen } from '../screens/analytics/AnalyticsOverviewScreen';
import { CompanyCashDashboard } from '../screens/analytics/CompanyCashDashboard';
import { ReconciliationDashboard } from '../screens/analytics/ReconciliationDashboard';
import CompanyExpenseTracker from '../screens/analytics/CompanyExpenseTracker';
import FinancialReportsHub from '../screens/analytics/FinancialReportsHub';
import { DailyDriverPayments } from '../screens/accounting/DailyDriverPayments';

// Import/Export Screens
import { ImportExportMainScreen } from '../screens/import-export/ImportExportMainScreen';
import { CaremImportScreen } from '../screens/import-export/CaremImportScreen';
import { UberImportScreen } from '../screens/import-export/UberImportScreen';
import { SalikImportScreen } from '../screens/import-export/SalikImportScreen';
import { FineImportScreen } from '../screens/import-export/FineImportScreen';
import { DriverExportScreen } from '../screens/import-export/DriverExportScreen';
import { VehicleExportScreen } from '../screens/import-export/VehicleExportScreen';

// Fixed Import Screens (Background-safe processing)
import CaremImportScreenFixed from '../screens/import-export/CaremImportScreenFixed';
import UberImportScreenFixed from '../screens/import-export/UberImportScreenFixed';
import SalikImportScreenFixed from '../screens/import-export/SalikImportScreenFixed';
import FineImportScreenFixed from '../screens/import-export/FineImportScreenFixed';

// Excel Upload Screens
import VehicleExcelUploadScreen from '../screens/import-export/VehicleExcelUploadScreen';
import DriverExcelUploadScreen from '../screens/import-export/DriverExcelUploadScreen';

// New CSV Import Screen with Verification System
import { CSVImportScreen } from '../screens/accounting/CSVImportScreen';

// Notifications Screen
import { NotificationsScreen } from '../screens/notifications/NotificationsScreen';
import { useNotificationSummary } from '../hooks/useNotifications';

// Navigation Context for web routing
import { NavigationProvider } from './NavigationContext';
import { JobCardProvider } from '../contexts/JobCardContext';

// Stack Navigators
const AuthStack = createStackNavigator();
const AccountingStack = createStackNavigator();
const ImportExportStack = createStackNavigator();
const MaintenanceStack = createStackNavigator();
const LiveMapStack = createStackNavigator();
const AssignmentHistoryStack = createStackNavigator();
const AnalyticsStack = createStackNavigator();
const SettingsStack = createStackNavigator();

// Screen Stack Navigators
const MainStack = createStackNavigator();

// Auth Navigator
const AuthNavigator = () => {
  return (
    <AuthStack.Navigator screenOptions={{ headerShown: false }}>
      <AuthStack.Screen name="Login" component={LoginScreen} />
      {/* Add other auth screens here */}
    </AuthStack.Navigator>
  );
};

// Accounting Navigator - Enhanced with new screens
const AccountingNavigator = ({ initialRouteName = 'AccountingView' }: { initialRouteName?: string }) => {
  return (
    <AccountingStack.Navigator 
      screenOptions={{ headerShown: false }}
      initialRouteName={initialRouteName}
    >
      <AccountingStack.Screen
        name="AccountingView"
        component={AccountingViewScreen}
        options={{ title: 'Accounting Overview' }}
      />
      <AccountingStack.Screen
        name="MonthlySettlement"
        component={MonthlySettlementScreen}
        options={{ title: 'Monthly Settlements' }}
      />
      <AccountingStack.Screen
        name="SettlementDetail"
        component={SettlementDetailScreen}
        options={{ title: 'Settlement Details' }}
      />
      <AccountingStack.Screen
        name="RentDiscounts"
        component={RentDiscountScreen}
        options={{ title: 'Share Adjustments' }}
      />
      <AccountingStack.Screen
        name="PaymentHistory"
        component={PaymentHistoryScreen}
        options={{ title: 'Payment History' }}
      />
      <AccountingStack.Screen
        name="AccountingList"
        component={AccountingScreen}
        options={{ title: 'Accounting Legacy' }}
      />
      <AccountingStack.Screen
        name="DailyPayments"
        component={DailyDriverPayments}
        options={{ title: 'Daily Driver Payments' }}
      />
      <AccountingStack.Screen
        name="DataImport"
        component={DataImportScreen}
        options={{ title: 'Data Import Legacy' }}
      />
    </AccountingStack.Navigator>
  );
};

// Import/Export Navigator
const ImportExportNavigator = () => {
  return (
    <ImportExportStack.Navigator screenOptions={{ headerShown: false }}>
      <ImportExportStack.Screen
        name="ImportExportMain"
        component={ImportExportMainScreen}
      />
      {/* New Advanced CSV Import with Verification */}
      <ImportExportStack.Screen
        name="CSVImport"
        component={CSVImportScreen}
        options={{ title: 'CSV Import & Verification' }}
      />
      <ImportExportStack.Screen
        name="CaremImport"
        component={CaremImportScreenFixed}
      />
      <ImportExportStack.Screen
        name="UberImport"
        component={UberImportScreenFixed}
      />
      <ImportExportStack.Screen
        name="SalikImport"
        component={SalikImportScreenFixed}
      />
      <ImportExportStack.Screen
        name="FineImport"
        component={FineImportScreenFixed}
      />
      <ImportExportStack.Screen
        name="DriverExport"
        component={DriverExportScreen}
      />
      <ImportExportStack.Screen
        name="VehicleExport"
        component={VehicleExportScreen}
      />
      {/* Excel Upload Screens */}
      <ImportExportStack.Screen
        name="VehicleExcelUpload"
        component={VehicleExcelUploadScreen}
        options={{ title: 'Vehicle Excel Upload' }}
      />
      <ImportExportStack.Screen
        name="DriverExcelUpload"
        component={DriverExcelUploadScreen}
        options={{ title: 'Driver Excel Upload' }}
      />
      {/* Original screens for comparison/fallback */}
      <ImportExportStack.Screen
        name="CaremImportOriginal"
        component={CaremImportScreen}
      />
      <ImportExportStack.Screen
        name="UberImportOriginal"
        component={UberImportScreen}
      />
      <ImportExportStack.Screen
        name="SalikImportOriginal"
        component={SalikImportScreen}
      />
      <ImportExportStack.Screen
        name="FineImportOriginal"
        component={FineImportScreen}
      />
    </ImportExportStack.Navigator>
  );
};

// Maintenance Navigator (placeholder)
const MaintenanceNavigator = ({ initialRouteName = 'MaintenanceDashboard' }: { initialRouteName?: string }) => {
  return (
    <MaintenanceStack.Navigator 
      screenOptions={{ headerShown: false }}
      initialRouteName={initialRouteName}
    >
      <MaintenanceStack.Screen
        name="MaintenanceDashboard"
        component={MaintenanceDashboardScreen}
        options={{ title: 'Maintenance Dashboard' }}
      />
      <MaintenanceStack.Screen
        name="MaintenanceJobCards"
        component={JobCardsScreen}
        options={{ title: 'Job Cards' }}
      />
      <MaintenanceStack.Screen
        name="JobCards"
        component={JobCardsScreen}
        options={{ title: 'Job Cards' }}
      />
      <MaintenanceStack.Screen
        name="MaintenanceCreditReport"
        component={WorkshopCreditReportScreen}
        options={{ title: 'Workshop Credit Report' }}
      />
      <MaintenanceStack.Screen
        name="MaintenanceList"
        component={MaintenanceScreen}
        options={{ title: 'Maintenance (Legacy)' }}
      />
      <MaintenanceStack.Screen
        name="AddJobCard"
        component={AddJobCardScreen}
      />
      <MaintenanceStack.Screen
        name="MaintenanceDetails"
        component={MaintenanceDetailsScreen}
      />
      <MaintenanceStack.Screen
        name="WorkshopCreditReport"
        component={WorkshopCreditReportScreen}
      />
      <MaintenanceStack.Screen
        name="VehicleAssignment"
        component={VehicleAssignmentScreen}
      />
    </MaintenanceStack.Navigator>
  );
};

// LiveMap Navigator (placeholder)
const LiveMapNavigator = () => {
  return (
    <LiveMapStack.Navigator screenOptions={{ headerShown: false }}>
      <LiveMapStack.Screen
        name="LiveMapView"
        component={() => (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text>Live Map Screen (Coming Soon)</Text>
          </View>
        )}
      />
    </LiveMapStack.Navigator>
  );
};

// Assignment History Navigator
const AssignmentHistoryNavigator = () => {
  return (
    <AssignmentHistoryStack.Navigator screenOptions={{ headerShown: false }}>
      <AssignmentHistoryStack.Screen
        name="AssignmentHistoryList"
        component={AssignmentHistoryScreen}
      />
      <AssignmentHistoryStack.Screen
        name="AssignmentDetails"
        component={AssignmentDetailsScreen as any}
      />
      <AssignmentHistoryStack.Screen
        name="ManageAssignment"
        component={ManageAssignmentScreen as any}
      />
      <AssignmentHistoryStack.Screen
        name="EditAssignment"
        component={EditAssignmentScreen as any}
      />
    </AssignmentHistoryStack.Navigator>
  );
};

// Analytics Navigator
// Analytics Navigator - Enhanced with new screens
const AnalyticsNavigator = ({ initialRouteName = 'AnalyticsOverview' }: { initialRouteName?: string }) => {
  return (
    <AnalyticsStack.Navigator 
      screenOptions={{ headerShown: false }}
      initialRouteName={initialRouteName}
    >
      <AnalyticsStack.Screen
        name="AnalyticsOverview"
        component={AnalyticsOverviewScreen}
        options={{ title: 'Analytics Overview' }}
      />
      <AnalyticsStack.Screen
        name="CompanyCash"
        component={CompanyCashDashboard}
        options={{ title: 'Company Cash Dashboard' }}
      />
      <AnalyticsStack.Screen
        name="Reconciliation"
        component={ReconciliationDashboard}
        options={{ title: 'Import Reconciliation' }}
      />
      <AnalyticsStack.Screen
        name="Expenses"
        component={CompanyExpenseTracker}
        options={{ title: 'Company Expenses' }}
      />
      <AnalyticsStack.Screen
        name="Reports"
        component={FinancialReportsHub}
        options={{ title: 'Financial Reports' }}
      />
      <AnalyticsStack.Screen
        name="CompanyProfit"
        component={CompanyProfitScreen}
        options={{ title: 'Company Profit Dashboard' }}
      />
      <AnalyticsStack.Screen
        name="AnalyticsDashboard"
        component={AnalyticsScreen}
        options={{ title: 'Analytics Legacy' }}
      />
    </AnalyticsStack.Navigator>
  );
};

// Settings Navigator
const SettingsNavigator = () => {
  return (
    <SettingsStack.Navigator screenOptions={{ headerShown: false }}>
      <SettingsStack.Screen
        name="SettingsMain"
        component={SettingsScreen}
        options={{ title: 'Settings' }}
      />
      <SettingsStack.Screen
        name="ActivityLog"
        component={ActivityLogScreen}
        options={{ title: 'Activity Log' }}
      />
      <SettingsStack.Screen
        name="VehicleRateSettings"
        component={VehicleRateSettingsScreen}
        options={{ title: 'Vehicle Pricing' }}
      />
      <SettingsStack.Screen
        name="DisplayOptions"
        component={DisplayOptionsScreen}
        options={{ title: 'Display Options' }}
      />
      <SettingsStack.Screen
        name="BackupExport"
        component={BackupExportScreen}
        options={{ title: 'Backup & Export' }}
      />
      <SettingsStack.Screen
        name="DriverSync"
        component={DriverSyncScreen}
        options={{ title: 'Driver Sync' }}
      />
      <SettingsStack.Screen
        name="UserManagement"
        component={UserManagementScreen}
        options={{ title: 'User Management' }}
      />
    </SettingsStack.Navigator>
  );
};

// Main Navigator with responsive design for both mobile and web
const MainNavigator = () => {
  // Use sessionStorage/localStorage to persist current route on web
  const getInitialRoute = () => {
    // Get user from authStore to determine initial route
    const currentUser = useAuthStore.getState().user;
    
    // For Operators: Always start at Assignments (their only screen)
    if (currentUser?.role === 'operator') {
      return 'Assignments';
    }
    
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Try to get route from URL path
      const path = window.location.pathname;
      const routeMap: Record<string, string> = {
        '/dashboard': 'Dashboard',
        '/vehicles': 'Vehicles',
        '/drivers': 'Drivers',
        '/assignments': 'Assignments',
        '/assignment-history': 'AssignmentHistory',
        '/analytics': 'Analytics',
        '/accounting': 'Accounting',
        '/import-export': 'ImportExport',
        '/maintenance': 'Maintenance',
        '/live-map': 'LiveMap',
        '/settings': 'Settings',
      };
      
      const route = routeMap[path] || sessionStorage.getItem('currentRoute') || 'Dashboard';
      return route;
    }
    return 'Dashboard';
  };
  
  const [currentRoute, setCurrentRoute] = useState(getInitialRoute());
  const [dbStatus, setDbStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [dbMessage, setDbMessage] = useState('');
  const [sidebarVisible, setSidebarVisible] = useState(false);
  
  // *** NEW: Expandable menu state ***
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['Analytics', 'Accounting']));
  
  // *** NEW: Track current section to manage navigator persistence ***
  const [currentSection, setCurrentSection] = useState<string>('Dashboard');
  
  // Update URL and storage when route changes
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathMap: Record<string, string> = {
        'Dashboard': '/dashboard',
        'Vehicles': '/vehicles',
        'Drivers': '/drivers',
        'Assignments': '/assignments',
        'AssignmentHistory': '/assignment-history',
        'Analytics': '/analytics',
        'Accounting': '/accounting',
        'ImportExport': '/import-export',
        'Maintenance': '/maintenance',
        'LiveMap': '/live-map',
        'Settings': '/settings',
      };
      
      const path = pathMap[currentRoute] || '/dashboard';
      
      // Update URL without page reload
      window.history.pushState({}, '', path);
      
      // Save to sessionStorage for page refreshes
      sessionStorage.setItem('currentRoute', currentRoute);
    }
  }, [currentRoute]);
  
  // Get screen dimensions and platform
  const screenWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web';
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  
  // Different layouts for mobile vs web
  const sidebarWidth = isWeb ? 280 : 240;
  const mobileSidebarWidth = Math.min(280, screenWidth * 0.75);

  // Test database connection on mount
  useEffect(() => {
    const checkDatabaseConnection = async () => {
      try {
        const result = await testSupabaseConnection();
        if (result.success) {
          setDbStatus('connected');
          setDbMessage('Database connected successfully');
        } else {
          setDbStatus('error');
          setDbMessage(result.message || 'Failed to connect to database');
        }
      } catch (error) {
        setDbStatus('error');
        setDbMessage('Database connection error');
        console.error('Database connection test failed:', error);
      }
    };

    checkDatabaseConnection();
  }, []);

  // Get permission checking methods from auth store
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const logout = useAuthStore((state) => state.logout);
  const checkSession = useAuthStore((state) => state.checkSession);
  const user = useAuthStore((state) => state.user);
  
  // Periodic session check to catch permission updates (runs silently in background)
  React.useEffect(() => {
    if (!user) return;
    
    // Check session every 2 minutes to catch permission changes
    const intervalId = setInterval(async () => {
      // Silent background check - won't block UI
      await checkSession();
    }, 120000); // 2 minutes
    
    // Initial check on mount (silent)
    checkSession();
    
    return () => clearInterval(intervalId);
  }, [user, checkSession]);
  
  // CRITICAL FIX: Emergency session validation on every render
  // If user object exists but is incomplete, force logout immediately
  React.useEffect(() => {
    if (user) {
      const hasValidData = user.id && 
                          user.name && 
                          user.name.trim() !== '' &&
                          user.email && 
                          user.role;
      
      if (!hasValidData) {
        console.error('🚨 CRITICAL: User session is incomplete! Forcing immediate logout');
        console.log('Invalid user data:', { 
          hasId: !!user.id, 
          hasName: !!(user.name && user.name.trim() !== ''), 
          hasEmail: !!user.email,
          hasRole: !!user.role,
          actualName: user.name,
        });
        
        // Force logout immediately
        logout().catch(console.error);
      }
    }
  }, [user, logout]);
  
  // Defensive: Ensure user has valid structure
  // Validate user has complete data for display
  const isValidUser = user && user.id && user.role && user.name && user.name.trim() !== '';
  const userName = isValidUser ? user.name : 'User';

  // *** ENHANCED: Menu items with sub-navigation support ***
  const allMenuItems = [
    { name: "Dashboard", icon: "home", href: "Dashboard", requiredPermission: PERMISSIONS.VIEW_DASHBOARD },
    { name: "Notifications", icon: "notifications", href: "Notifications", requiredPermission: PERMISSIONS.VIEW_DASHBOARD, badge: true },
    { name: "Vehicles", icon: "car", href: "Vehicles", requiredPermission: PERMISSIONS.VIEW_VEHICLES },
    { name: "Drivers", icon: "people", href: "Drivers", requiredPermission: PERMISSIONS.VIEW_DRIVERS },
    { name: "Assignments", icon: "document-text", href: "Assignments", requiredPermission: PERMISSIONS.VIEW_ASSIGNMENTS },
    { name: "Assignment History", icon: "clipboard", href: "AssignmentHistory", requiredPermission: PERMISSIONS.VIEW_ASSIGNMENTS },
    
    // *** NEW: Analytics section with sub-menu ***
    { 
      name: "Analytics", 
      icon: "bar-chart", 
      href: "Analytics",
      requiredPermission: PERMISSIONS.VIEW_REPORTS,
      isExpandable: true,
      subItems: [
        { name: "Overview", href: "AnalyticsOverview", icon: "stats-chart" },
        { name: "Company Cash", href: "CompanyCash", icon: "cash" },
        { name: "Reconciliation", href: "Reconciliation", icon: "shield-checkmark" },
        { name: "Expenses", href: "Expenses", icon: "card" },
        { name: "Reports", href: "Reports", icon: "document-text" },
        { name: "Company Profit", href: "CompanyProfit", icon: "trending-up" },
      ]
    },
    
    // *** NEW: Accounting section with sub-menu ***
    { 
      name: "Accounting", 
      icon: "calculator", 
      href: "Accounting",
      requiredPermission: PERMISSIONS.VIEW_ACCOUNTING,
      isExpandable: true,
      subItems: [
        { name: "Overview", href: "AccountingOverview", icon: "grid" },
        { name: "Daily Payments", href: "DailyPayments", icon: "wallet" },
        { name: "Monthly Settlements", href: "MonthlySettlements", icon: "document-text" },
        { name: "Share Adjustments", href: "RentDiscounts", icon: "pricetag" },
        { name: "Payment History", href: "PaymentHistory", icon: "cash" },
      ]
    },
    
    { name: "Import/Export", icon: "cloud-upload", href: "ImportExport", requiredPermission: PERMISSIONS.EDIT_ACCOUNTING },
    
    // *** NEW: Maintenance section with sub-menu ***
    { 
      name: "Maintenance", 
      icon: "construct", 
      href: "Maintenance",
      requiredPermission: PERMISSIONS.VIEW_MAINTENANCE,
      isExpandable: true,
      subItems: [
        { name: "Dashboard", href: "MaintenanceDashboard", icon: "speedometer" },
        { name: "Job Cards", href: "MaintenanceJobCards", icon: "clipboard" },
        { name: "Credit Report", href: "MaintenanceCreditReport", icon: "document-text" },
      ]
    },
    
    // *** NEW: HR Management section with sub-menu ***
    { 
      name: "HR Management", 
      icon: "people-circle", 
      href: "HRManagement",
      requiredPermission: PERMISSIONS.VIEW_HR,
      isExpandable: true,
      subItems: [
        { name: "Employees", href: "EmployeeList", icon: "person" },
        { name: "Contracts", href: "Contracts", icon: "document" },
        { name: "Documents", href: "Documents", icon: "folder-open" },
        { name: "Leave Management", href: "LeaveManagement", icon: "calendar" },
        { name: "Payroll", href: "Payroll", icon: "wallet" },
        { name: "Attendance", href: "Attendance", icon: "time" },
        { name: "Performance", href: "PerformanceReviews", icon: "stats-chart" },
      ]
    },
    
    { name: "Live Map", icon: "map", href: "LiveMap", requiredPermission: PERMISSIONS.VIEW_MAINTENANCE },
    { name: "Settings", icon: "settings", href: "Settings", requiredPermission: PERMISSIONS.VIEW_SETTINGS },
  ];

  // Filter menu items based on user permissions (only if valid user)
  const menuItems = isValidUser ? allMenuItems.filter(item => {
    if (!item.requiredPermission) return true; // No permission required
    return hasPermission(item.requiredPermission);
  }) : [];

  const renderCurrentScreen = () => {
    // For web, render screens directly instead of using nested navigators with initialRouteName
    // This ensures proper screen switching without navigator state issues
    const isWeb = Platform.OS === 'web';
    
    if (isWeb) {
      // Direct screen rendering for web platform - no navigator state conflicts
      switch (currentRoute) {
        // Analytics screens
        case 'Analytics':
        case 'AnalyticsOverview':
          return <AnalyticsOverviewScreen />;
        case 'CompanyProfit':
          return <CompanyProfitScreen />;
        
        // Accounting screens
        case 'Accounting':
        case 'AccountingOverview':
          return <AccountingViewScreen />;
        case 'MonthlySettlements':
          return <MonthlySettlementScreen />;
        case 'RentDiscounts':
          return <RentDiscountScreen />;
        case 'PaymentHistory':
          return <PaymentHistoryScreen />;
        
        // Maintenance screens
        case 'Maintenance':
        case 'MaintenanceDashboard':
          return <MaintenanceDashboardScreen />;
        case 'MaintenanceJobCards':
          return <JobCardsScreen />;
        case 'MaintenanceCreditReport':
          return <CreditReportScreen />;
        case 'AddJobCard':
          return <AddJobCardScreen />;
        case 'MaintenanceDetails':
          return <MaintenanceDetailsScreen />;
        
        // HR Management screens
        case 'HRManagement':
        case 'EmployeeList':
          return <EmployeeListScreen />;
        case 'Contracts':
          return <Text style={{ padding: 20, color: '#666' }}>Contracts screen - Coming soon</Text>;
        case 'Documents':
          return <Text style={{ padding: 20, color: '#666' }}>Documents screen - Coming soon</Text>;
        case 'LeaveManagement':
          return <Text style={{ padding: 20, color: '#666' }}>Leave Management screen - Coming soon</Text>;
        case 'Payroll':
          return <Text style={{ padding: 20, color: '#666' }}>Payroll screen - Coming soon</Text>;
        case 'Attendance':
          return <Text style={{ padding: 20, color: '#666' }}>Attendance screen - Coming soon</Text>;
        case 'PerformanceReviews':
          return <Text style={{ padding: 20, color: '#666' }}>Performance Reviews screen - Coming soon</Text>;
        
        // Other navigators (keep as navigators for nested screens)
        case 'Dashboard':
          return <DashboardNavigator />;
        case 'Notifications':
          return <NotificationsScreen />;
        case 'Vehicles':
          return <VehicleNavigator />;
        case 'Drivers':
          return <DriverNavigator />;
        case 'Assignments':
          return <AssignmentNavigator />;
        case 'AssignmentHistory':
          return <AssignmentHistoryNavigator />;
        
        case 'ImportExport':
          return <ImportExportNavigator />;
        case 'LiveMap':
          return <LiveMapNavigator />;
        case 'Settings':
          return <SettingsNavigator />;
        default:
          return <DashboardNavigator />;
      }
    } else {
      // Mobile: Use navigators with proper key management
      let section = currentRoute;
      let initialRoute = currentRoute;
      
      // Map routes to their sections and initial routes
      if (['Analytics', 'AnalyticsOverview', 'CompanyProfit'].includes(currentRoute)) {
        section = 'Analytics';
        initialRoute = currentRoute === 'CompanyProfit' ? 'CompanyProfit' : 'AnalyticsOverview';
      } else if (['Accounting', 'AccountingOverview', 'MonthlySettlements', 'RentDiscounts', 'PaymentHistory'].includes(currentRoute)) {
        section = 'Accounting';
        if (currentRoute === 'MonthlySettlements') initialRoute = 'MonthlySettlement';
        else if (currentRoute === 'RentDiscounts') initialRoute = 'RentDiscounts';
        else if (currentRoute === 'PaymentHistory') initialRoute = 'PaymentHistory';
        else initialRoute = 'AccountingView';
      } else if (['Maintenance', 'MaintenanceDashboard', 'MaintenanceJobCards', 'MaintenanceCreditReport', 'JobCards', 'AddJobCard', 'MaintenanceDetails'].includes(currentRoute)) {
        section = 'Maintenance';
        if (currentRoute === 'MaintenanceJobCards' || currentRoute === 'JobCards') initialRoute = 'MaintenanceJobCards';
        else if (currentRoute === 'MaintenanceCreditReport') initialRoute = 'MaintenanceCreditReport';
        else if (currentRoute === 'AddJobCard') initialRoute = 'AddJobCard';
        else if (currentRoute === 'MaintenanceDetails') initialRoute = 'MaintenanceDetails';
        else initialRoute = 'MaintenanceDashboard';
      } else if (['HRManagement', 'EmployeeList', 'Contracts', 'Documents', 'LeaveManagement', 'Payroll', 'Attendance', 'PerformanceReviews'].includes(currentRoute)) {
        section = 'HRManagement';
        initialRoute = currentRoute === 'HRManagement' ? 'EmployeeList' : currentRoute;
      }
      
      switch (section) {
        case 'Dashboard':
          return <DashboardNavigator />;
        case 'Notifications':
          return <NotificationsScreen />;
        case 'Vehicles':
          return <VehicleNavigator />;
        case 'Drivers':
          return <DriverNavigator />;
        case 'Assignments':
          return <AssignmentNavigator />;
        case 'AssignmentHistory':
          return <AssignmentHistoryNavigator />;
        
        case 'Analytics':
          return <AnalyticsNavigator key={`analytics-${initialRoute}`} initialRouteName={initialRoute} />;
        
        case 'Accounting':
          return <AccountingNavigator key={`accounting-${initialRoute}`} initialRouteName={initialRoute} />;
        
        case 'Maintenance':
          return <MaintenanceNavigator key={`maintenance-${initialRoute}`} initialRouteName={initialRoute} />;
        
        case 'HRManagement':
          // For mobile, HR always shows EmployeeList as entry point
          return <EmployeeListScreen />;
        
        case 'ImportExport':
          return <ImportExportNavigator />;
        case 'LiveMap':
          return <LiveMapNavigator />;
        case 'Settings':
          return <SettingsNavigator />;
        default:
          return <DashboardNavigator />;
      }
    }
  };

  const getCurrentPageTitle = () => {
    const page = menuItems.find(nav => nav.href === currentRoute);
    return page ? page.name : "Dashboard";
  };

  // Web Sidebar Component (Fixed sidebar for web)
  const WebSidebar = () => (
    <View style={{
      width: sidebarWidth,
      backgroundColor: theme.colors.white,
      borderRightWidth: 1,
      borderRightColor: theme.colors.gray200,
      height: '100%',
    }}>
      {/* Sidebar Header */}
      <View style={{
        padding: theme.spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.gray200,
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Ionicons name="car" size={24} color={theme.colors.primary} />
          <Text style={{
            fontSize: theme.typography.sizes.lg,
            fontWeight: '600',
            color: theme.colors.textPrimary,
          }}>
            STSC Fleet
          </Text>
        </View>
      </View>
      
      {/* Sidebar Content */}
      <View style={{ padding: theme.spacing.md, flex: 1 }}>
        {/* Navigation Menu with Expandable Sub-Items */}
        <View>
          {menuItems.map((item: any) => (
            <View key={item.name}>
              {/* Main Menu Item */}
              <TouchableOpacity
                onPress={() => {
                  if (item.isExpandable) {
                    // Toggle expansion
                    const newExpanded = new Set(expandedMenus);
                    if (newExpanded.has(item.name)) {
                      newExpanded.delete(item.name);
                    } else {
                      newExpanded.add(item.name);
                    }
                    setExpandedMenus(newExpanded);
                  } else {
                    // Navigate directly
                    setCurrentRoute(item.href);
                  }
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 12,
                  paddingHorizontal: 12,
                  marginBottom: item.isExpandable ? 4 : 4,
                  borderRadius: 8,
                  backgroundColor: !item.isExpandable && currentRoute === item.href 
                    ? theme.colors.primary + '15' 
                    : 'transparent',
                }}
              >
                <Ionicons 
                  name={(!item.isExpandable && currentRoute === item.href) ? item.icon as any : `${item.icon}-outline` as any}
                  size={16} 
                  color={(!item.isExpandable && currentRoute === item.href) ? theme.colors.primary : theme.colors.gray400}
                />
                <Text style={{
                  marginLeft: 12,
                  flex: 1,
                  fontSize: 14,
                  color: (!item.isExpandable && currentRoute === item.href) ? theme.colors.primary : theme.colors.gray600,
                  fontWeight: (!item.isExpandable && currentRoute === item.href) ? '500' : '400',
                }}>
                  {item.name}
                </Text>
                {/* Expandable indicator */}
                {item.isExpandable && (
                  <Ionicons 
                    name={expandedMenus.has(item.name) ? "chevron-down" : "chevron-forward"}
                    size={14}
                    color={theme.colors.gray400}
                  />
                )}
              </TouchableOpacity>

              {/* Sub-Items (only if expanded) */}
              {item.isExpandable && expandedMenus.has(item.name) && item.subItems && (
                <View style={{ marginLeft: 28, marginBottom: 8 }}>
                  {item.subItems.map((subItem: any) => {
                    const isActive = currentRoute === subItem.href;
                    return (
                      <TouchableOpacity
                        key={subItem.name}
                        onPress={() => setCurrentRoute(subItem.href)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          paddingVertical: 10,
                          paddingHorizontal: 12,
                          marginBottom: 2,
                          borderRadius: 6,
                          backgroundColor: isActive ? theme.colors.primary + '10' : 'transparent',
                        }}
                      >
                        <Ionicons 
                          name={isActive ? subItem.icon as any : `${subItem.icon}-outline` as any}
                          size={14}
                          color={isActive ? theme.colors.primary : theme.colors.gray400}
                        />
                        <Text style={{
                          marginLeft: 10,
                          fontSize: 13,
                          color: isActive ? theme.colors.primary : theme.colors.gray600,
                          fontWeight: isActive ? '500' : '400',
                        }}>
                          {subItem.name}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          ))}
        </View>
        
        {/* Add Vehicle Button - Only show if user has permission */}
        {hasPermission(PERMISSIONS.CREATE_VEHICLES) && (
          <View style={{ marginTop: 24 }}>
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.primary,
                paddingVertical: 10,
                paddingHorizontal: 16,
                borderRadius: 8,
                gap: 8,
              }}
              onPress={() => setCurrentRoute('AddVehicle')}
            >
              <Ionicons name="add" size={16} color={theme.colors.white} />
              <Text style={{
                fontSize: 14,
                fontWeight: '500',
                color: theme.colors.white,
              }}>
                Add Vehicle
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Bottom Section - User Info & Logout */}
      <View style={{
        padding: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.gray200,
      }}>
        {/* User Info */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          paddingVertical: 8,
        }}>
          <View style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: theme.colors.primary + '20',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Ionicons name="person" size={16} color={theme.colors.primary} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{
              fontSize: 13,
              fontWeight: '600',
              color: theme.colors.textPrimary,
            }} numberOfLines={1}>
              {userName}
            </Text>
            <Text style={{
              fontSize: 11,
              color: theme.colors.textSecondary,
            }} numberOfLines={1}>
              {user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Text>
          </View>
        </View>

        {/* Logout Button */}
        <TouchableOpacity 
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fee',
            paddingVertical: 10,
            paddingHorizontal: 16,
            borderRadius: 8,
            gap: 8,
            borderWidth: 1,
            borderColor: '#fcc',
          }}
          onPress={async () => {
            await logout();
          }}
        >
          <Ionicons name="log-out-outline" size={16} color={theme.colors.red600} />
          <Text style={{
            fontSize: 14,
            fontWeight: '500',
            color: theme.colors.red600,
          }}>
            Logout
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Mobile Sidebar Component (Modal overlay for mobile)
  const MobileSidebar = () => (
    <Modal
      visible={sidebarVisible}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setSidebarVisible(false)}
    >
      <View style={{ flex: 1, flexDirection: 'row' }}>
        {/* Overlay */}
        <TouchableOpacity 
          style={{ 
            flex: 1, 
            backgroundColor: 'rgba(0,0,0,0.5)' 
          }}
          onPress={() => setSidebarVisible(false)}
        />
        
        {/* Sidebar */}
        <View style={{
          width: mobileSidebarWidth,
          backgroundColor: theme.colors.white,
          height: '100%',
          paddingTop: 40, // Account for status bar
        }}>
          {/* Mobile Sidebar Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing.md,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.gray200,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Ionicons name="car" size={24} color={theme.colors.primary} />
              <Text style={{
                fontSize: theme.typography.sizes.lg,
                fontWeight: '600' as any,
                color: theme.colors.textPrimary,
              }}>
                STSC Fleet
              </Text>
            </View>
            <TouchableOpacity onPress={() => setSidebarVisible(false)}>
              <Ionicons name="close" size={24} color={theme.colors.gray400} />
            </TouchableOpacity>
          </View>
          
          {/* Mobile Sidebar Content */}
          <View style={{ padding: theme.spacing.md, flex: 1 }}>
            {/* Navigation Menu */}
            <View>
              {menuItems.map((item) => (
                <TouchableOpacity
                  key={item.name}
                  onPress={() => {
                    setCurrentRoute(item.href);
                    setSidebarVisible(false);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: 16,
                    paddingHorizontal: 12,
                    marginBottom: 4,
                    borderRadius: 8,
                    backgroundColor: currentRoute === item.href ? theme.colors.primary + '15' : 'transparent',
                  }}
                >
                  <Ionicons 
                    name={currentRoute === item.href ? item.icon as any : `${item.icon}-outline` as any}
                    size={20} 
                    color={currentRoute === item.href ? theme.colors.primary : theme.colors.gray400}
                  />
                  <Text style={{
                    marginLeft: 16,
                    fontSize: 16,
                    color: currentRoute === item.href ? theme.colors.primary : theme.colors.gray600,
                    fontWeight: currentRoute === item.href ? '500' : '400',
                  }}>
                    {item.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            
            {/* Add Vehicle Button - Only show if user has permission */}
            {hasPermission(PERMISSIONS.CREATE_VEHICLES) && (
              <View style={{ marginTop: 24 }}>
                <TouchableOpacity 
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.colors.primary,
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    gap: 8,
                  }}
                  onPress={() => {
                    setCurrentRoute('AddVehicle');
                    setSidebarVisible(false);
                  }}
                >
                  <Ionicons name="add" size={16} color={theme.colors.white} />
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '500',
                    color: theme.colors.white,
                  }}>
                    Add Vehicle
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Bottom Section - User Info & Logout (Mobile) */}
          <View style={{
            padding: theme.spacing.md,
            borderTopWidth: 1,
            borderTopColor: theme.colors.gray200,
          }}>
            {/* User Info */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
              paddingVertical: 8,
            }}>
              <View style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: theme.colors.primary + '20',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Ionicons name="person" size={18} color={theme.colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme.colors.textPrimary,
                }} numberOfLines={1}>
                  {userName}
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme.colors.textSecondary,
                }} numberOfLines={1}>
                  {user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Text>
              </View>
            </View>

            {/* Logout Button */}
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fee',
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                gap: 8,
                borderWidth: 1,
                borderColor: '#fcc',
              }}
              onPress={async () => {
                setSidebarVisible(false);
                await logout();
              }}
            >
              <Ionicons name="log-out-outline" size={18} color={theme.colors.red600} />
              <Text style={{
                fontSize: 15,
                fontWeight: '500',
                color: theme.colors.red600,
              }}>
                Logout
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Main Content Area
  const MainContent = () => {
    // Fetch notification summary for badge
    const { data: notificationSummary } = useNotificationSummary();
    const unreadCount = notificationSummary?.unread || 0;
    const criticalCount = (notificationSummary?.bySeverity.critical || 0) + (notificationSummary?.bySeverity.high || 0);

    return (
    <View style={{ flex: 1, backgroundColor: theme.colors.gray50 }}>
      {/* Header */}
      <View style={{
        backgroundColor: theme.colors.white,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.gray200,
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            {/* Mobile Menu Button (only show on mobile) */}
            {isMobile && (
              <TouchableOpacity
                onPress={() => setSidebarVisible(true)}
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: theme.colors.gray100,
                  borderRadius: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name="menu" size={20} color={theme.colors.gray600} />
              </TouchableOpacity>
            )}
            
            <Text style={{
              fontSize: isMobile ? 18 : 20,
              fontWeight: '600',
              color: theme.colors.textPrimary,
            }}>
              {getCurrentPageTitle()}
            </Text>
          </View>
          
          {/* Right Side: Notification Bell & Database Status */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            {/* Notification Bell with Badge */}
            <TouchableOpacity
              onPress={() => setCurrentRoute('Notifications')}
              style={{
                position: 'relative',
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: currentRoute === 'Notifications' ? theme.colors.primary + '15' : theme.colors.gray100,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ionicons 
                name={unreadCount > 0 ? "notifications" : "notifications-outline"} 
                size={20} 
                color={currentRoute === 'Notifications' ? theme.colors.primary : theme.colors.gray600}
              />
              {/* Badge for unread notifications */}
              {unreadCount > 0 && (
                <View style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  backgroundColor: criticalCount > 0 ? theme.colors.red600 : theme.colors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: 4,
                  borderWidth: 2,
                  borderColor: theme.colors.white,
                }}>
                  <Text style={{
                    fontSize: 10,
                    fontWeight: '600',
                    color: theme.colors.white,
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>

            {/* Database Status Indicator */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: dbStatus === 'connected' ? '#10b981' : 
                                dbStatus === 'error' ? '#ef4444' : '#f59e0b'
              }} />
              <Text style={{
                fontSize: isMobile ? 10 : 12,
                color: dbStatus === 'connected' ? '#10b981' : 
                       dbStatus === 'error' ? '#ef4444' : '#f59e0b',
                fontWeight: '500'
              }}>
                {isMobile 
                  ? (dbStatus === 'connected' ? 'DB OK' : dbStatus === 'error' ? 'DB Error' : 'Loading...')
                  : (dbStatus === 'connected' ? 'Database Connected' : 
                     dbStatus === 'error' ? 'Database Error' : 'Connecting...')
                }
              </Text>
            </View>
          </View>
        </View>
      </View>
      
      {/* Content */}
      <View style={{ flex: 1, padding: isMobile ? 12 : 24 }}>
        {renderCurrentScreen()}
      </View>
    </View>
  );
};

  // Return different layouts for mobile vs web
  if (isMobile) {
    return (
      <JobCardProvider>
        <NavigationProvider value={{ currentRoute, setCurrentRoute }}>
          <View style={{ flex: 1, height: '100%' }}>
            <MobileSidebar />
            <MainContent />
          </View>
        </NavigationProvider>
      </JobCardProvider>
    );
  }

  // Web layout with fixed sidebar
  return (
    <JobCardProvider>
      <NavigationProvider value={{ currentRoute, setCurrentRoute }}>
        <View style={{ flex: 1, flexDirection: 'row', height: '100%' }}>
          <WebSidebar />
          <MainContent />
        </View>
      </NavigationProvider>
    </JobCardProvider>
  );
};

// Main App Navigator
const AppNavigator = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthEnabled = useAuthStore((state) => state.isAuthEnabled);
  
  // Simple loading state - just wait for initial hydration
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simple initialization - authStore handles session restoration via persist
    const initApp = async () => {
      console.log('🚀 AppNavigator: Initializing...');
      
      // Just wait a moment for Zustand persist to rehydrate from localStorage
      // The authStore's onRehydrateStorage already handles session restoration
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('✅ AppNavigator: Initialization complete, user:', user?.name || 'none');
      setIsLoading(false);
    };
    
    initApp();
  }, []);

  // Show loading ONLY while initializing (not during background session checks)
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 10, color: theme.colors.textPrimary }}>
          Loading...
        </Text>
      </View>
    );
  }

  // CRITICAL FIX: Validate user has complete data BEFORE rendering MainNavigator
  // This prevents showing dashboard with incomplete user data
  const hasValidUser = user && 
                      user.id && 
                      user.name && 
                      user.name.trim() !== '' &&
                      user.email && 
                      user.role;
  
  // Only consider user authenticated if they have COMPLETE valid data
  const isAuthenticated = hasValidUser || !isAuthEnabled;
  
  return isAuthenticated ? <MainNavigator /> : <AuthNavigator />;
};

export default AppNavigator;
